/** AppSwitcher: the 9-dot launcher showing only real SaaSBOXX entitlements, degrading to an empty state. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpRight, Settings, X, Loader2 } from 'lucide-react';

const ENTITLEMENT_PATH = '/api/auth/me/apps/';
const TOKEN_LS_KEYS = ['sessionToken', 'access_token', 'token', 'authToken'];
const CACHE_LS_KEY = 'restoboxx_appsw_cache_v1';
const CACHE_MAX_AGE_MS = 10 * 60 * 1000;

const EMPTY_DATA = { tenant: null, user: null, apps: [], admin_console_url: null };

function readToken() {
  for (const k of TOKEN_LS_KEYS) {
    const v = localStorage.getItem(k);
    if (v) return v;
  }
  return null;
}

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_LS_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() - entry.fetchedAt > CACHE_MAX_AGE_MS) return null;
    return entry;
  } catch { return null; }
}

function writeCache(entry) {
  try { sessionStorage.setItem(CACHE_LS_KEY, JSON.stringify(entry)); } catch { /* quota */ }
}

const NineDotIcon = ({ className = '' }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={`w-5 h-5 ${className}`} aria-hidden="true">
    {[3, 10, 17].flatMap((y) =>
      [3, 10, 17].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r={1.6} />)
    )}
  </svg>
);

const TileIcon = ({ tile }) => {
  if (tile.icon_url) {
    return (
      <img
        src={tile.icon_url}
        alt=""
        className="h-9 w-9 rounded object-contain"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    );
  }
  if (tile.icon) return <span className="text-2xl leading-none">{tile.icon}</span>;
  const accent = tile.accent_color || '#6b7280';
  return (
    <div
      className="h-9 w-9 rounded flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
      style={{ backgroundColor: accent }}
    >
      {tile.name.charAt(0)}
    </div>
  );
};

const AppSwitcher = ({ currentProductSlug = 'restoboxx' }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const popoverRef = useRef(null);
  const inFlight = useRef(false);

  // Prime from cache on mount
  useEffect(() => {
    const c = readCache();
    if (c) setData(c.body);
  }, []);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Fetch on first open
  useEffect(() => {
    if (!open || inFlight.current) return;
    const token = readToken();
    if (!token) {
      setData((prev) => prev || EMPTY_DATA);
      return;
    }
    if (!data) setLoading(true);
    inFlight.current = true;
    fetch(ENTITLEMENT_PATH, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = await r.json();
        setData(body);
        writeCache({ fetchedAt: Date.now(), body });
      })
      .catch((err) => {
        console.warn('[AppSwitcher] fetch failed:', err);
        setData((prev) => prev || EMPTY_DATA);
      })
      .finally(() => { inFlight.current = false; setLoading(false); });
  }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps

  const tiles = useMemo(() => {
    const list = (data?.apps || []).filter(
      (t) => t.slug !== currentProductSlug && t.status !== 'suspended'
    );
    list.sort((a, b) => (a.order || 0) - (b.order || 0));
    return list;
  }, [data, currentProductSlug]);

  const handleTileClick = async (tile, e) => {
    if (tile.status === 'suspended') { e.preventDefault(); return; }
    if (tile.slug === currentProductSlug) { e.preventDefault(); setOpen(false); return; }

    // Custom tiles and tiles without an sso_handoff_url just navigate to the bare URL.
    if (tile.status === 'custom' || !tile.sso_handoff_url) {
      setOpen(false);
      return;
    }

    e.preventDefault();
    setOpen(false);
    const token = readToken();
    if (!token) {
      window.location.href = tile.url;
      return;
    }
    try {
      const resp = await fetch('/api/v1/auth/sso/handoff/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ target: tile.slug }),
      });
      if (!resp.ok) {
        console.warn('[AppSwitcher] handoff mint failed', resp.status);
        window.location.href = tile.url;
        return;
      }
      const data = await resp.json();
      if (!data || !data.token) {
        window.location.href = tile.url;
        return;
      }
      const u = new URL(tile.sso_handoff_url);
      u.searchParams.set('token', data.token);
      window.location.href = u.toString();
    } catch (err) {
      console.warn('[AppSwitcher] handoff failed', err);
      window.location.href = tile.url;
    }
  };

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Switch app"
        title="Switch app"
        className="p-2 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
      >
        <NineDotIcon />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[340px] bg-card border rounded-xl shadow-2xl z-50 overflow-hidden"
          role="dialog"
          aria-label="App switcher"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Your apps</div>
              <div className="text-sm font-medium text-foreground">
                {data?.tenant?.name || 'Tenant'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 rounded text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Tiles grid */}
          <div className="p-3 grid grid-cols-3 gap-2 max-h-[360px] overflow-y-auto">
            {loading && (
              <div className="col-span-3 flex items-center justify-center py-8 text-muted-foreground text-sm">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading apps…
              </div>
            )}
            {!loading && tiles.length === 0 && (
              <div className="col-span-3 text-center text-sm text-muted-foreground py-8">
                No other apps available
              </div>
            )}
            {!loading && tiles.map((tile) => (
              <a
                key={tile.slug}
                href={tile.url || '#'}
                onClick={(e) => handleTileClick(tile, e)}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-lg hover:bg-accent transition-colors text-center ${tile.status === 'suspended' ? 'opacity-40 cursor-not-allowed' : ''}`}
                title={tile.tagline || tile.name}
              >
                <TileIcon tile={tile} />
                <div className="text-xs font-medium text-foreground line-clamp-1 w-full">
                  {tile.name}
                </div>
                {tile.tagline && (
                  <div className="text-[10px] text-muted-foreground line-clamp-1 w-full">
                    {tile.tagline}
                  </div>
                )}
              </a>
            ))}
          </div>

          {/* Footer */}
          {data?.admin_console_url && (
            <div className="border-t bg-muted/30">
              <a
                href={data.admin_console_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between px-4 py-2.5 text-sm text-foreground hover:bg-accent transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Manage account
                </span>
                <ArrowUpRight className="w-4 h-4 text-muted-foreground" />
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AppSwitcher;
